import './config/setupGlobals'

import { FC, useState } from 'react'
import {
  AppBarProps as MuiAppBarProps,
  Box,
  Helmet,
  HelmetProvider,
  PerkThemeProvider,
  styled,
} from '@perk-ui/core'
import { QueryClientProvider } from 'react-query'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'

import AppErrorBoundary from './components/AppErrorBoundary'
import AppHeader from './components/AppHeader'
import AppSidebar from './components/AppSidebar'
import SitesProvider from './components/SitesProvider'
import queryClient from './config/react-query'
import { templateTheme } from './config/theme'
import AuthProvider from './features/auth/AuthContext'
import Routes from './pages/Routes'

const drawerWidth = 320

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
  onMenuClick?: () => void
}

const AppBar = styled(AppHeader, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    [theme.breakpoints.up('md')]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
}))

const App: FC = () => {
  // const classes = useStyles()
  const [open, setOpen] = useState(() => window.innerWidth >= 900)

  return (
    <HelmetProvider>
      <PerkThemeProvider theme={templateTheme}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <QueryParamProvider ReactRouterRoute={Route}>
              <Helmet
                titleTemplate="%s | DataBiologics Impact"
                defaultTitle="DataBiologics"
              />
              <AuthProvider>
                <SitesProvider>
                  <AppErrorBoundary>
                    <Box sx={{ display: 'flex', height: '100vh' }}>
                      <AppBar
                        open={open}
                        onMenuClick={() => setOpen((prev) => !prev)}
                      />
                      <AppSidebar
                        open={open}
                        setOpen={setOpen}
                        drawerWidth={drawerWidth}
                      />
                      <Routes />
                    </Box>
                  </AppErrorBoundary>
                </SitesProvider>
              </AuthProvider>
            </QueryParamProvider>
          </Router>
        </QueryClientProvider>
      </PerkThemeProvider>
    </HelmetProvider>
  )
}

export default App
